import React, { Component, createRef } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getStyle, mobile } from 'styles';
import { setMap } from 'utils/map';
import Controls from 'containers/Controls';

const MapContainer = styled.div`
  flex-grow: 1;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  .mapboxgl-map {
    height: 100%;
    width: 100%;
  }
  .mapboxgl-ctrl > button {
    border-radius: 0 !important;
  }
  .mapboxgl-popup {
    width: ${getStyle('popupWidth')};
    height: ${getStyle('popupMaxHeight')};
    ${mobile(`
      height: ${getStyle('popupMaxHeightMobile')};
    `)};
  }
  .mapboxgl-popup-content {
    padding: 0;
  }
  .mapboxgl-ctrl-top-right {
    top: unset;
    bottom: 50%;
    margin-bottom: calc(-43.5px + 20px);
  }
`;

class Map extends Component {
  mapRef = createRef();

  map = null;

  static propTypes = {
    clearSelection: PropTypes.func.isRequired,
    hoverFeature: PropTypes.func.isRequired,
    isInitialized: PropTypes.bool.isRequired,
    isMapLoaded: PropTypes.bool.isRequired,
    mapConfig: PropTypes.object,
    mapLayers: PropTypes.array.isRequired,
    mapLoaded: PropTypes.func.isRequired,
    selectFeature: PropTypes.func.isRequired,
    unhoverFeature: PropTypes.func.isRequired,
  };

  static defaultProps = {
    mapConfig: {},
  };

  state = {
    controlsAdded: false,
  };

  componentDidUpdate({ isInitialized: wasInitialized }) {
    const { isInitialized } = this.props;
    if (!wasInitialized && isInitialized) this.initialize();
  }

  initialize = () => {
    const { mapConfig, isMapLoaded } = this.props;
    if (isMapLoaded) return;
    this.map = setMap(
      new mapboxgl.Map({
        ...mapConfig,
        container: this.mapRef.current,
      }),
    );

    this.map.on('load', this.onMapLoaded);
  };

  addLayers = () => {
    const {
      mapLayers,
      hoverFeature,
      unhoverFeature,
      selectFeature,
    } = this.props;
    mapLayers.forEach(layer => {
      this.map.addLayer(layer);
      this.map.on('mousemove', layer.id, hoverFeature);
      this.map.on('mouseleave', layer.id, unhoverFeature);
      this.map.on('click', layer.id, selectFeature);
    });
  };

  addControls = () => {
    this.setState({ controlsAdded: true });
    this.map.addControl(
      new mapboxgl.NavigationControl(),
      'top-right',
    );
  };

  getLayer = id =>
    new Promise(resolve => {
      const resolver = () => {
        const layer = this.map.getLayer(id);
        resolve(layer);
        this.map.off('idle', resolver);
      };
      this.map.on('idle', resolver);
    });

  onMapLoaded = () => {
    const { clearSelection, mapLoaded, mapLayers } = this.props;
    this.addLayers();
    this.addControls();
    this.map.on('idle', () => mapLoaded());
    this.map.on('click', e => {
      if (
        this.map.queryRenderedFeatures(e.point, {
          layers: mapLayers.map(layer => layer.id),
        }).length === 0
      )
        clearSelection();
    });
  };

  render() {
    const { className, css } = this.props;
    const { controlsAdded } = this.state;
    return (
      <MapContainer className={className} css={css}>
        <div id="mapbox-map" ref={this.mapRef} />
        {controlsAdded && <Controls />}
      </MapContainer>
    );
  }
}

export default Map;
